import { DecimalPipe, UpperCasePipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import type { AbstractControl, NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxSuggestion } from '@onyx/angular';
import {
  FormHelper,
  OnyxDatepickerComponent,
  OnyxDatePipe,
  OnyxFormGroupComponent,
  OnyxFormMode,
  onyxMaxDateValidator,
  onyxMaxNumberValidator,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { SuggestionHelper } from '../../../../../../../common/helpers/suggestions.helper';
import { ValidationHelper } from '../../../../../../../common/helpers/validation.helper';
import type { PreferencesService } from '../../../../../../../common/services/preferences.service';
import { OrderStatus } from '../../../../../common/enums/order-status';
import { OrderHelper } from '../../../../../common/helpers/order.helper';
import type { Order } from '../../../../../common/interfaces/order';
import { OrdersService } from '../../../../../common/services/orders.service';

type OrderModalPaymentFormGroup = ReturnType<
  (typeof OrderModalPaymentFormComponent)['buildForm']
>;

export type OrderModalPaymentForm = ReturnType<
  OrderModalPaymentFormGroup['getRawValue']
>;

@Component({
  selector: 'app-order-modal-payment-form',
  imports: [
    OnyxDatepickerComponent,
    OnyxToggleComponent,
    OnyxTextFieldComponent,
    OnyxSuggestionsComponent,
    ReactiveFormsModule,
    OnyxFormGroupComponent,
    UpperCasePipe,
  ],
  templateUrl: './order-modal-payment-form.component.html',
  styleUrl: './order-modal-payment-form.component.scss',
})
export class OrderModalPaymentFormComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly ordersService = inject(OrdersService);

  public readonly order = input.required<Order>();
  public readonly form = input.required<OrderModalPaymentFormGroup>();
  public readonly mode = input.required<OnyxFormMode>();

  public readonly partiallyPaidChanged = output<boolean>();

  protected readonly I18N = 'orders.orderModal';
  protected readonly DATE_SUGGESTIONS = SuggestionHelper.getDateSuggestions(
    this.translateService,
    this.datePipe,
    { future: false },
  );

  protected readonly OrderStatus = OrderStatus;

  protected showVat = signal(true);
  protected netCurrency = signal<string | null>(null);
  protected vatCurrency = signal<string | null>(null);
  protected remainingNetSuggestions = signal<OnyxSuggestion<number>[] | null>(
    null,
  );
  protected remainingVatSuggestions = signal<OnyxSuggestion<number>[] | null>(
    null,
  );

  protected isPartiallyPaid = linkedSignal(() =>
    [OrderStatus.PARTIALLY_PAID, OrderStatus.PAID].includes(
      this.order().status.value,
    ),
  );
  protected paymentDetails = computed(() =>
    OrderHelper.getPaymentDetails(this.order().payment),
  );

  public ngOnInit(): void {
    const { controls } = this.form();
    const { payment, status } = this.order();
    const invoice = payment?.invoice;

    const netCurrency = invoice?.net?.currency ?? null;
    const vatCurrency = invoice?.vat?.currency ?? null;

    const netValueControls = controls.net.controls;
    const vatValueControls = controls.vat.controls;

    this.ordersService.reload$.subscribe(() => this.form().reset());

    this.netCurrency.set(netCurrency);
    this.vatCurrency.set(vatCurrency);

    if (invoice?.vat?.value === 0) {
      this.showVat.set(false);
      ValidationHelper.addValidators(
        netValueControls.value,
        Validators.required,
      );
    }

    const resetCurrency = (): void => {
      netValueControls.currency.setValue(netCurrency);
      vatValueControls.currency.setValue(vatCurrency);
    };

    const toggleRequiredValidators = (): void => {
      const paymentDetails = this.paymentDetails();
      if (!paymentDetails || this.mode() !== OnyxFormMode.ADD) return;

      const { remainingNet, remainingVat } = paymentDetails;

      ValidationHelper.toggleRequiredValidator(
        netValueControls.value,
        remainingVat === 0,
      );
      ValidationHelper.toggleRequiredValidator(
        vatValueControls.value,
        remainingNet === 0,
      );
    };

    effect(
      () => {
        const isPartiallyPaid = this.isPartiallyPaid();
        const paymentDetails = this.paymentDetails();

        ValidationHelper.toggleControls([controls.net, controls.vat], {
          enabled: isPartiallyPaid,
          reset: true,
        });

        resetCurrency();
        this.partiallyPaidChanged.emit(isPartiallyPaid);

        if (!paymentDetails || this.mode() !== OnyxFormMode.ADD) return;

        const { remainingNet, remainingVat } = paymentDetails;

        if (remainingNet === 0) {
          ValidationHelper.disableControls([controls.net], { reset: true });
        }
        if (remainingVat === 0) {
          ValidationHelper.disableControls([controls.vat], { reset: true });
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const paymentDetails = this.paymentDetails();
        if (!paymentDetails) return;

        const { remainingNet, remainingVat, invoiceNetPrice, invoiceVatPrice } =
          paymentDetails;
        const currentStatus = status.value;

        const updateSuggestionsAndValidators = (
          remaining: number,
          setSuggestions: (
            suggestions: OnyxSuggestion<number>[] | null,
          ) => void,
          control: AbstractControl,
        ) => {
          const mode = this.mode();

          if (remaining === 0) {
            setSuggestions(null);
            toggleRequiredValidators();

            if (mode === OnyxFormMode.EDIT) {
              if (control.value != null) {
                control.addValidators(
                  onyxMaxNumberValidator(control.value / 100, 2),
                );
              } else {
                ValidationHelper.disableControls(control, { reset: false });
              }
            }
            return;
          }

          const maxPossibleValue =
            mode === OnyxFormMode.EDIT ? remaining + control.value : remaining;
          setSuggestions([
            {
              name: this.decimalPipe.transform(maxPossibleValue / 100)!,
              value: maxPossibleValue,
            },
          ]);
          ValidationHelper.clearValidators(control);
          toggleRequiredValidators();

          control.addValidators(
            onyxMaxNumberValidator(maxPossibleValue / 100, 2),
          );
        };

        if (
          [OrderStatus.PARTIALLY_PAID, OrderStatus.PAID].includes(currentStatus)
        ) {
          updateSuggestionsAndValidators(
            remainingNet,
            this.remainingNetSuggestions.set,
            netValueControls.value,
          );
          updateSuggestionsAndValidators(
            remainingVat,
            this.remainingVatSuggestions.set,
            vatValueControls.value,
          );
        } else if (currentStatus === OrderStatus.WAITING_FOR_PAYMENT) {
          netValueControls.value.addValidators(
            onyxMaxNumberValidator(invoiceNetPrice / 100, 2),
          );
          vatValueControls.value.addValidators(
            onyxMaxNumberValidator(invoiceVatPrice / 100, 2),
          );
        }
      },
      { injector: this.injector },
    );
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    preferencesService: PreferencesService,
  ) {
    return fb.group({
      date: fb.control<string | null>(null, [
        Validators.required,
        onyxMaxDateValidator(),
      ]),
      net: FormHelper.buildAmountForm(fb, preferencesService, {
        optional: true,
      }),
      vat: FormHelper.buildAmountForm(fb, preferencesService, {
        optional: true,
      }),
    });
  }
}
